import Router from 'next/router';
import type { UrlObject } from 'url';
import allowlist from '../route-allowlist.json';

const ALLOWED = new RegExp(allowlist.combinedRegexWithQuery);
export const FALLBACK = '/home';

function getCurrentPathAndSearch(): { path: string; search: string } {
  if (typeof window !== 'undefined') {
    return {
      path: window.location.pathname || '/',
      search: window.location.search || '',
    };
  }
  // SSR fallback; Router.push is only used client-side, but keep it sane.
  return { path: '/', search: '' };
}

function serializeQuery(q: UrlObject['query']): string {
  if (!q || typeof q !== 'object') return '';
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(q)) {
    if (v === undefined || v === null) continue;
    if (Array.isArray(v)) {
      for (const item of v) {
        if (item === undefined || item === null) continue;
        usp.append(k, String(item));
      }
    } else {
      usp.set(k, String(v));
    }
  }
  const s = usp.toString();
  return s ? `?${s}` : '';
}

function toPathAndSearch(href: string | UrlObject): string | null {
  const base = typeof window !== 'undefined' ? window.location.origin : 'http://localhost';

  if (typeof href === 'string') {
    const s = href.trim();
    // ✅ NEW: allow pure hash links
    if (s.startsWith('#')) {
      const current =
        typeof window !== 'undefined' ? window.location.pathname + window.location.search : '/';
      return current + s;
    }

    const lower = s.toLowerCase();
    if (lower.startsWith('javascript:') || lower.startsWith('data:') || lower.startsWith('//')) {
      console.error('Potential XSS detected!');
      return null;
    }
    try {
      const u = new URL(s, base);
      if ((lower.startsWith('http:') || lower.startsWith('https:')) && u.origin !== base) {
        console.error('Protocol/origin mismatch between origin and routing destination');
        return null;
      }
      return (u.pathname || '/') + (u.search || '') + (u.hash || '');
    } catch {
      const [pAndQ, hash = ''] = s.split('#', 2);
      const [p, q] = pAndQ.split('?', 2);
      const pathOnly = p.startsWith('/') ? p : '/' + p;
      const search = q ? `?${q}` : '';
      const h = hash ? `#${hash}` : '';
      return pathOnly + search + h;
    }
  }

  // UrlObject case
  const current = getCurrentPathAndSearch();
  const pathname =
    (href.pathname && (href.pathname.startsWith('/') ? href.pathname : '/' + href.pathname)) ||
    current.path ||
    '/';
  const search = serializeQuery(href.query);
  const hash = href.hash ? (href.hash.startsWith('#') ? href.hash : `#${href.hash}`) : '';

  // ✅ NEW: handle hash-only object
  if (!href.pathname && !href.query && href.hash) {
    return pathname + current.search + hash;
  }

  return pathname + search + hash;
}

export function sanitize(href: string | UrlObject, fallback: string): string {
  const cand = toPathAndSearch(href);
  if (!cand) return fallback;

  // Ignore the hash for allowlist matching
  const withoutHash = cand.includes('#') ? cand.slice(0, cand.indexOf('#')) : cand;

  // If the path+query is allowed, return full candidate (with hash preserved)
  return ALLOWED.test(withoutHash) ? cand : fallback;
}

type PushOrReplace = typeof Router.push;

/**
 * Use this function to apply input sanitization for the default next-router.
 *
 * This prevents XSS attacks by only allowing forwards to routes known to the project.
 * The route definition and the corresponding regex can be found in the auto-generated file
 * `route-allowlist.json`
 */
export function hardenRouter(options?: { fallback?: string; throwOnBlock?: boolean }) {
  const fallback = options?.fallback ?? FALLBACK;
  const throwOnBlock = !!options?.throwOnBlock;

  const wrap = (fn: PushOrReplace): PushOrReplace =>
    ((url: any, as?: any, opts?: any) => {
      const safeUrl = sanitize(url, fallback);
      const safeAs = as !== undefined ? sanitize(as, fallback) : safeUrl;
      if (throwOnBlock && (safeUrl === fallback || safeAs === fallback)) {
        return Promise.reject(new Error('Blocked unsafe navigation'));
      }
      return fn(safeUrl, safeAs, opts);
    }) as PushOrReplace;

  (Router as any).push = wrap(Router.push.bind(Router));
  (Router as any).replace = wrap(Router.replace.bind(Router));

  Router.ready(() => {
    const r: any = (Router as any).router;
    if (!r) return;

    const bindWrap = (orig: any) => (url: any, as?: any, opts?: any) => {
      const safeUrl = sanitize(url, fallback);
      const safeAs = as !== undefined ? sanitize(as, fallback) : safeUrl;
      if (throwOnBlock && (safeUrl === fallback || safeAs === fallback)) {
        return Promise.reject(new Error('Blocked unsafe navigation'));
      }
      return orig(safeUrl, safeAs, opts);
    };

    r.push = bindWrap(r.push.bind(r));
    r.replace = bindWrap(r.replace.bind(r));

    if (typeof r.prefetch === 'function') {
      const origPrefetch = r.prefetch.bind(r);
      r.prefetch = (url: any, asPath?: any, opts?: any) => {
        const safe = sanitize(url, fallback);
        const withoutHash = safe.includes('#') ? safe.slice(0, safe.indexOf('#')) : safe;
        return origPrefetch(withoutHash, undefined, opts);
      };
    }
  });
}
